import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api, unwrap } from '@/api/client';
import type { components } from '@/api/schema';
import { Badge } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Field, RootError, Section } from '@/components/ui/form-controls';
import { Input } from '@/components/ui/input';
import { applyProblemToForm } from '@/lib/forms';
import { tenantKeys, useTenantMutation } from './api';
import { consentSchema, type ConsentForm } from './schemas';
import './pilotI18n';

type ConsentDto = components['schemas']['ConsentDto'];
type Status = '' | 'optedIn' | 'optedOut';

/**
 * WhatsApp consent registry (ADR 0021): list, manual opt-out, attested manual opt-in, CSV export. Opt-outs from STOP
 * land here automatically. The CSV import is API-only for now (POST …/consents/whatsapp/import, attested).
 */
export function ConsentsTab({ tenantId }: { tenantId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [status, setStatus] = useState<Status>('');
  const list = useQuery({
    queryKey: [...tenantKeys.part(tenantId, 'consents'), status],
    queryFn: async () =>
      unwrap(
        await api.GET('/v1/tenants/{id}/consents/whatsapp', {
          params: { path: { id: tenantId }, query: { status: status || undefined, limit: 200 } },
        }),
      ),
  });

  const form = useForm<ConsentForm>({
    resolver: zodResolver(consentSchema(t)),
    defaultValues: { number: '', status: 'optedOut', attestation: '', contactRef: '' },
  });
  const save = useTenantMutation(tenantId, async (v: ConsentForm) =>
    unwrap(
      await api.POST('/v1/tenants/{id}/consents/whatsapp', {
        params: { path: { id: tenantId } },
        body: { number: v.number, status: v.status, attestation: v.attestation || null, contactRef: v.contactRef || null },
      }),
    ),
  );
  const onSubmit = form.handleSubmit(async (v) => {
    try {
      await save.mutateAsync(v);
      form.reset({ number: '', status: v.status, attestation: '', contactRef: '' });
      await qc.invalidateQueries({ queryKey: tenantKeys.part(tenantId, 'consents') });
    } catch (err) {
      applyProblemToForm(form, err, { fields: ['number', 'status', 'attestation', 'contactRef'] });
    }
  });

  const exportCsv = async () => {
    const res = await api.GET('/v1/tenants/{id}/consents/whatsapp/export', { params: { path: { id: tenantId } }, parseAs: 'text' });
    const blob = new Blob([String(res.data ?? '')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'whatsapp-consents.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const { errors, isSubmitting } = form.formState;
  const items: ConsentDto[] = list.data?.items ?? [];
  const chosen = form.watch('status');

  return (
    <div className="flex flex-col gap-8">
      <Section
        title={t('pilot.consents.title')}
        description={t('pilot.consents.description')}
        actions={
          <div className="flex gap-2">
            <select
              aria-label={t('pilot.consents.status')}
              className="h-8 rounded-md border bg-background px-2 text-xs"
              value={status}
              onChange={(e) => setStatus(e.target.value as Status)}
            >
              <option value="">{t('pilot.consents.filterAll')}</option>
              <option value="optedIn">{t('pilot.consents.optedIn')}</option>
              <option value="optedOut">{t('pilot.consents.optedOut')}</option>
            </select>
            <Button variant="outline" size="sm" onClick={() => void exportCsv()}>
              {t('pilot.consents.export')}
            </Button>
          </div>
        }
      >
        {items.length === 0 ? (
          <p className="text-xs text-muted-foreground">{t('pilot.consents.empty')}</p>
        ) : (
          <table className="w-full text-xs" data-testid="consents-table">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="py-1">{t('pilot.consents.number')}</th>
                <th>{t('pilot.consents.status')}</th>
                <th>{t('pilot.consents.source')}</th>
                <th>{t('pilot.consents.at')}</th>
                <th>{t('pilot.consents.by')}</th>
                <th>{t('pilot.consents.evidence')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="border-t">
                  <td className="py-1 font-mono">{c.number}</td>
                  <td>
                    <Badge variant={c.status === 'optedOut' ? 'destructive' : 'success'}>
                      {c.status === 'optedOut' ? t('pilot.consents.optedOut') : t('pilot.consents.optedIn')}
                    </Badge>
                  </td>
                  <td>{c.source}</td>
                  <td>{new Date(c.at).toLocaleString()}</td>
                  <td className="max-w-40 truncate">{c.recordedBy}</td>
                  <td className="max-w-64 truncate" title={c.evidence ?? ''}>
                    {c.evidence}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section title={chosen === 'optedIn' ? t('pilot.consents.markOptIn') : t('pilot.consents.markOptOut')}>
        <form className="grid max-w-xl gap-3" onSubmit={(e) => void onSubmit(e)} noValidate>
          <Field label={t('pilot.consents.number')} htmlFor="consent-number" error={errors.number?.message}>
            <Input id="consent-number" className="font-mono" aria-invalid={!!errors.number} {...form.register('number')} />
          </Field>
          <Field label={t('pilot.consents.status')} htmlFor="consent-status">
            <select id="consent-status" className="h-9 rounded-md border bg-background px-2 text-sm" {...form.register('status')}>
              <option value="optedOut">{t('pilot.consents.optedOut')}</option>
              <option value="optedIn">{t('pilot.consents.optedIn')}</option>
            </select>
          </Field>
          {chosen === 'optedIn' && (
            <Field label={t('pilot.consents.attestation')} htmlFor="consent-attestation" error={errors.attestation?.message}>
              <Input id="consent-attestation" aria-invalid={!!errors.attestation} {...form.register('attestation')} />
            </Field>
          )}
          <Field label={t('pilot.consents.contactRef')} htmlFor="consent-ref">
            <Input id="consent-ref" className="font-mono" {...form.register('contactRef')} />
          </Field>
          <RootError message={errors.root?.message} />
          <div>
            <Button type="submit" disabled={isSubmitting}>
              {chosen === 'optedIn' ? t('pilot.consents.markOptIn') : t('pilot.consents.markOptOut')}
            </Button>
          </div>
        </form>
      </Section>
    </div>
  );
}
